import type { StepConfig, StepStatus } from './VerticalStepForm.model';

export type SubscriptionChannel = 'canSubmit' | 'activeStep' | 'counts' | `step:${string}`;

/**
 * Pure state machine managing step progression for the VerticalStepForm.
 * Tracks step statuses, enforces sequential progression, and notifies
 * subscribers via per-channel EventTarget notifications.
 */
export class StepFormEngine {
  readonly #steps: Array<StepConfig>;
  readonly #states: Map<string, StepStatus>;
  #activeStepId: string | null;
  readonly #eventTarget = new EventTarget();

  constructor(steps: Array<StepConfig>) {
    if (steps.length === 0) {
      throw new Error('StepFormEngine requires at least one step');
    }

    const ids = new Set<string>();
    steps.forEach((step) => {
      if (ids.has(step.id)) {
        throw new Error(`StepFormEngine: duplicate step id "${step.id}"`);
      }
      ids.add(step.id);
    });

    this.#steps = steps;
    this.#states = new Map();

    steps.forEach((step, index) => {
      this.#states.set(step.id, index === 0 ? 'in_progress' : 'pending');
    });

    this.#activeStepId = steps[0].id;
  }

  getStepStatus(stepId: string): StepStatus {
    return this.#states.get(stepId) ?? 'pending';
  }

  getActiveStepId(): string | null {
    return this.#activeStepId;
  }

  /** Returns true when all required steps have been completed. */
  canSubmit(): boolean {
    return this.#steps.filter((step) => step.required).every((step) => this.#states.get(step.id) === 'completed');
  }

  getCompletedCount(): number {
    let count = 0;
    this.#states.forEach((status) => {
      if (status === 'completed') {
        count++;
      }
    });
    return count;
  }

  getSkippedCount(): number {
    let count = 0;
    this.#states.forEach((status) => {
      if (status === 'skipped') {
        count++;
      }
    });
    return count;
  }

  /** Subscribe to a specific channel. Returns an unsubscribe function. */
  subscribe(channel: SubscriptionChannel, listener: () => void): () => void {
    this.#eventTarget.addEventListener(channel, listener);
    return () => this.#eventTarget.removeEventListener(channel, listener);
  }

  /** Revert a completed step to in_progress (e.g. when its value changes). */
  uncomplete(stepId: string): void {
    if (!this.#states.has(stepId)) {
      return;
    }
    if (this.#states.get(stepId) !== 'completed') {
      return;
    }
    this.#states.set(stepId, 'in_progress');
    this.#notify(`step:${stepId}`);
    this.#notify('canSubmit');
    this.#notify('counts');
  }

  /** Mark a step as completed and activate the next step in sequence. */
  advance(stepId: string): void {
    if (!this.#states.has(stepId)) {
      return;
    }
    const currentStatus = this.#states.get(stepId);
    if (currentStatus === 'pending') {
      return;
    }

    if (currentStatus !== 'completed') {
      this.#states.set(stepId, 'completed');
      this.#notify(`step:${stepId}`);
      this.#notify('canSubmit');
      this.#notify('counts');
    }
    this.#activateNextStep(stepId);
  }

  /** Mark an optional step as skipped and activate the next step. */
  skip(stepId: string): void {
    if (!this.#states.has(stepId)) {
      return;
    }
    const step = this.#steps.find((s) => s.id === stepId);
    if (!step || step.required) {
      return;
    }

    const currentStatus = this.#states.get(stepId);
    if (currentStatus === 'pending') {
      return;
    }

    this.#states.set(stepId, 'skipped');
    this.#notify(`step:${stepId}`);
    this.#notify('counts');
    this.#activateNextStep(stepId);
  }

  /** Expand a previously visited step for viewing or editing. */
  goToStep(stepId: string): void {
    if (!this.#states.has(stepId)) {
      return;
    }
    const targetStatus = this.#states.get(stepId);
    if (targetStatus === 'pending') {
      return;
    }

    if (targetStatus === 'skipped') {
      this.#states.set(stepId, 'in_progress');
      this.#notify(`step:${stepId}`);
      this.#notify('counts');
    }

    this.#activeStepId = stepId;
    this.#notify('activeStep');
  }

  /** Reset all steps to initial state. */
  reset(): void {
    this.#steps.forEach((step, index) => {
      this.#states.set(step.id, index === 0 ? 'in_progress' : 'pending');
      this.#notify(`step:${step.id}`);
    });
    this.#activeStepId = this.#steps[0]?.id ?? null;
    this.#notify('activeStep');
    this.#notify('canSubmit');
    this.#notify('counts');
  }

  #activateNextStep(afterStepId: string): void {
    const currentIndex = this.#steps.findIndex((s) => s.id === afterStepId);
    const nextStep = this.#steps[currentIndex + 1];

    if (nextStep) {
      if (this.#states.get(nextStep.id) === 'pending') {
        this.#states.set(nextStep.id, 'in_progress');
        this.#notify(`step:${nextStep.id}`);
      }
      this.#activeStepId = nextStep.id;
    } else {
      this.#activeStepId = null;
    }
    this.#notify('activeStep');
  }

  #notify(channel: SubscriptionChannel): void {
    this.#eventTarget.dispatchEvent(new Event(channel));
  }
}
